import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Col, Row, Text } from 'elmo-elements';
import {
  AssignmentLateOutlinedIcon,
  CheckOutlinedIcon,
  CloseOutlinedIcon,
  SimpleBadge,
  WarningOutlinedIcon,
} from 'element';
import {
  AccountTreeSelector,
  PreferencesDateFormat,
  ShiftOfferProposal as ShiftOfferProposalModel,
  ShiftOfferWithProposals,
  StringMap,
  UserFields,
} from 'type';
import { StoreState } from 'state/types';
import {
  ApproveOrDeclineShiftOfferPayload,
  BOX_SHIFT_OFFER_APPROVE_SHIFT_OFFER_PROPOSAL,
  BOX_SHIFT_OFFER_DECLINE_SHIFT_OFFER_PROPOSAL,
  getIsOutdatedValue,
  getShiftOfferWithProposals,
} from 'state/ManagerDashboard/ShiftOffers/ShiftOffer';
import { userListSelector } from 'state/UsersCollection';
import { getUserName } from 'lib/helpers';
import { getAccountTree } from 'state/AccountTree';
import { getDateFormat } from 'state/Account';
import moment from 'moment';
import { DutyHolderChangeHint } from 'page/ManagerDashboard/components/DutyHolderChangeHint';
import { DutyCoverageConfirmModal } from 'page/ManagerDashboard/components/DutyCoverageConfirmModal';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Avatar,
} from 'oxygen-elements';

type OwnProps = {
  proposal: ShiftOfferProposalModel;
};

type StateProps = {
  userList: StringMap<UserFields>;
  isOutdated: boolean;
  shiftOffer: ShiftOfferWithProposals;
  tree: AccountTreeSelector;
  dateFormat: PreferencesDateFormat;
};

type DispatchProps = {
  approveProposal: (payload: ApproveOrDeclineShiftOfferPayload) => void;
  declineProposal: (payload: ApproveOrDeclineShiftOfferPayload) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  showDutyCoverageModal: boolean;
};

class ShiftOfferProposalComponent extends Component<Props, State> {
  state: State = { showDutyCoverageModal: false };

  private colProps = {
    xs: 24 / 2,
    md: 24 / 4,
    className: 'mb-1 mb-xs-2 mb-md-4',
  };

  render() {
    const { proposal } = this.props;

    return (
      <Card
        role="row"
        className="shift-offer-proposal-item"
        data-proposal-id={proposal.id}
      >
        <CardHeader
          className="card-header"
          avatar={
            <Avatar
              light
              src={this.user.avatar.src}
              alt={getUserName(this.user)}
              size={'small'}
            />
          }
          title={getUserName(this.user)}
          titleTypographyProps={{
            fontSize: 16,
            fontWeight: 500,
          }}
          action={
            <SimpleBadge
              type={'warning'}
              icon={<AssignmentLateOutlinedIcon />}
              label={'Pending'}
            />
          }
        />
        <CardContent className="card-content">
          <Row>
            <Col {...this.colProps}>
              <Text color="gray" size="sm">
                Prev / next shift (hrs)
              </Text>
              <div>
                {proposal.prev_shift_hours !== null && (
                  <span data-testid="prev-hours">
                    -{proposal.prev_shift_hours}
                  </span>
                )}
                &nbsp;/&nbsp;
                {proposal.next_shift_hours !== null && (
                  <span data-testid="next-hours">
                    +{proposal.next_shift_hours}
                  </span>
                )}
              </div>
            </Col>

            <Col {...this.colProps}>
              <Text color="gray" size="sm">
                Total shift (hrs)
              </Text>
              <div data-testid="total-hours">{proposal.total_shift_hours}</div>
            </Col>

            <Col {...this.colProps}>
              <Text color="gray" size="sm">
                Projected (hrs)
              </Text>
              <div data-testid="projected-hours">
                {proposal.projected_shift_hours}
              </div>
            </Col>

            <Col {...this.colProps}>
              <Text color="gray" size="sm">
                Min hrs roster
              </Text>
              <div data-testid="min-hours">{proposal.min_hours_per_roster}</div>
            </Col>
          </Row>
          {!!proposal.messages.length && (
            <div className={this.colProps.className}>
              <div>
                <Text color="warning">
                  <WarningOutlinedIcon />
                </Text>
                <Text color="gray" size="sm">
                  &nbsp;Warning
                </Text>
              </div>
              <div data-testid="warnings">
                {proposal.messages.map((message, messageIndex) => (
                  <div key={messageIndex}>- {message}</div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
        {this.renderDutyHolderHint()}
        {this.renderDutyCoverageModal()}
        <CardActions className="card-actions">
          {this.actions.map((action, key) => (
            <Button
              id={action.id}
              key={`btn-${action.label}`}
              size="large"
              onClick={action.onClick}
              disabled={action.isDisabled}
            >
              {action.icon} {action.label}
            </Button>
          ))}
        </CardActions>
      </Card>
    );
  }

  private get actions() {
    const { proposal, isOutdated } = this.props;
    return [
      {
        label: 'Decline',
        onClick: this.declineProposal(proposal.id),
        icon: <CloseOutlinedIcon />,
        isDisabled: isOutdated,
        id: 'decline',
      },
      {
        label: 'Approve',
        onClick: this.onClickApprove(proposal.id),
        icon: <CheckOutlinedIcon />,
        isDisabled: isOutdated || proposal.is_error,
        id: 'approve',
      },
    ];
  }

  private renderDutyHolderHint(): React.ReactNode {
    const { shiftOffer } = this.props;
    if (shiftOffer.duty_coverage?.status === 'coverage_met') {
      return <DutyHolderChangeHint />;
    }
    return null;
  }

  private renderDutyCoverageModal(): React.ReactNode {
    const { showDutyCoverageModal } = this.state;
    const { proposal, shiftOffer, tree, dateFormat } = this.props;

    if (!showDutyCoverageModal || !shiftOffer.duty_coverage) {
      return null;
    }

    const { duty_coverage } = shiftOffer;
    const location = tree.sites[shiftOffer.site_id]?.name ?? '';
    const parsedStart = moment(shiftOffer.shift_start);
    const dayDate = `${parsedStart.format('ddd')}, ${parsedStart.format(dateFormat)}`;

    return (
      <DutyCoverageConfirmModal
        open
        location={location}
        dayDate={dayDate}
        shortfalls={duty_coverage.shortfalls}
        onApproveAnyway={this.onConfirmApprove(proposal.id)}
        onReject={this.onConfirmReject(proposal.id)}
      />
    );
  }

  private get user(): UserFields {
    const {
      proposal: { user_id },
      userList,
    } = this.props;

    return userList[user_id];
  }

  private onClickApprove = (proposalId: string) => () => {
    const { isOutdated, shiftOffer } = this.props;
    if (isOutdated) return;

    if (shiftOffer.duty_coverage?.warning === true) {
      this.setState({ showDutyCoverageModal: true });
    } else {
      this.props.approveProposal({ proposalId });
    }
  };

  private onConfirmApprove = (proposalId: string) => () => {
    this.setState({ showDutyCoverageModal: false });
    this.props.approveProposal({ proposalId });
  };

  private onConfirmReject = (proposalId: string) => () => {
    this.setState({ showDutyCoverageModal: false });
    this.props.declineProposal({ proposalId });
  };

  private declineProposal = (proposalId: string) => () => {
    const { declineProposal, isOutdated } = this.props;
    if (!isOutdated) {
      declineProposal({ proposalId });
    }
  };
}

const mapStateToProps = (state: StoreState): StateProps => ({
  userList: userListSelector(state),
  isOutdated: getIsOutdatedValue(state),
  shiftOffer: getShiftOfferWithProposals(state),
  tree: getAccountTree(state),
  dateFormat: getDateFormat(state),
});

const mapDispatchToProps: DispatchProps = {
  approveProposal: BOX_SHIFT_OFFER_APPROVE_SHIFT_OFFER_PROPOSAL,
  declineProposal: BOX_SHIFT_OFFER_DECLINE_SHIFT_OFFER_PROPOSAL,
};

export const ShiftOfferProposal = connect(
  mapStateToProps,
  mapDispatchToProps
)(ShiftOfferProposalComponent);
